import { EmptyStateIcon } from '../../../assets/EmptyState/EmptyStateIcon';

export function EmptyState({
  handleOnboardingNavigate,
  steps = [],
  role,
  recommendations = [],
}: {
  handleOnboardingNavigate: (stepKey?: string) => void;
  steps?: { key: string; status: string; value?: any }[];
  role?: string;
  recommendations?: string[];
}) {
  const canonicalStatus = (raw?: string) => {
    const v = String(raw || '').toLowerCase();
    if (v === 'completed' || v === 'complete') return 'completed';
    if (v === 'in_progress') return 'in_progress';
    return 'outstanding';
  };

  const stepMeta = [
    { key: 'initial_setup', label: 'Company basics' },
  ];

  const stepStatusMap = new Map(
    (steps || []).map((s) => [String(s.key), canonicalStatus(s.status)]),
  );

  const checklist = stepMeta.map((s) => ({
    ...s,
    status: stepStatusMap.get(s.key) || 'outstanding',
  }));

  const completedCount = checklist.filter((s) => s.status === 'completed').length;
  const totalCount = checklist.length;
  const progressPct =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const nextStep =
    checklist.find((s) => s.status !== 'completed')?.key || checklist[0]?.key;

  return (
    <>
      <div className="flex items-center justify-between border-b-2 border-[#E4E4E7] pb-4">
        <h1 className="text-2xl font-bold  text-tremor-content-strong">Home</h1>
      </div>
      <div className="flex min-h-[85vh] flex-col items-center justify-center gap-4 px-4">
        <EmptyStateIcon />
        <div className="w-full max-w-xl text-center">
          <h1 className="text-3xl font-bold">Set up your account</h1>
          <p className="mt-2 text-gray-600">
            Add a few essentials to unlock your dashboard.
          </p>
          {role && (
            <p className="mt-1 text-sm text-gray-500">
              Personalized setup for role: <span className="font-medium capitalize">{role}</span>
            </p>
          )}

          <div className="mt-6 rounded-xl border bg-white p-4 text-left shadow-sm">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-900">
                Setup progress
              </div>
              <div className="text-sm text-gray-600">
                {completedCount}/{totalCount} ({progressPct}%)
              </div>
            </div>
            <div className="mt-2 h-2 w-full rounded-full bg-gray-100">
              <div
                className="h-2 rounded-full bg-[#DC2626]"
                style={{ width: `${progressPct}%` }}
              />
            </div>

            <ul className="mt-4 space-y-2">
              {checklist.map((step) => (
                <li key={step.key} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex h-2.5 w-2.5 rounded-full ${
                        step.status === 'completed'
                          ? 'bg-green-500'
                          : step.status === 'in_progress'
                            ? 'bg-yellow-500'
                            : 'bg-gray-300'
                      }`}
                    />
                    <span className="text-sm text-gray-800">{step.label}</span>
                  </div>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      step.status === 'completed'
                        ? 'bg-green-50 text-green-700'
                        : step.status === 'in_progress'
                          ? 'bg-yellow-50 text-yellow-700'
                          : 'bg-gray-50 text-gray-600'
                    }`}
                  >
                    {step.status.replace('_', ' ')}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {recommendations.length > 0 && (
            <div className="mt-4 rounded-xl border bg-white p-4 text-left shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900">Recommended next actions</h3>
              <ul className="mt-2 space-y-1">
                {recommendations.map((item) => (
                  <li key={item} className="text-sm text-gray-700">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
            <button
              onClick={() => handleOnboardingNavigate(nextStep)}
              className="rounded-lg bg-[#DC2626] px-4 py-2 text-white"
            >
              Continue setup
            </button>
            <button
              onClick={() => handleOnboardingNavigate()}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700"
            >
              View setup
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
